import React, { useEffect, useRef } from 'react';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import 'xterm/css/xterm.css';
import './Terminal.css';
import { API_BASE_URL } from '../config.js';

interface TerminalProps {
  onClose?: () => void;
}

export const Terminal: React.FC<TerminalProps> = ({ onClose }) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (!terminalRef.current) return;

    mountedRef.current = true;
    let term = xtermRef.current;
    let fitAddon = fitAddonRef.current;
    let ws = wsRef.current;

    // Only create new terminal if none exists
    if (!term) {
      term = new XTerm({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: '"Cascadia Code", "Fira Code", "Consolas", "Courier New", monospace',
        theme: {
          background: '#000000',
          foreground: '#ffffff',
          cursor: '#ffffff',
          cursorAccent: '#000000',
          selectionBackground: '#ffffff40',
        },
        rows: 30,
        cols: 100,
      });

      // Add addons
      fitAddon = new FitAddon();
      const webLinksAddon = new WebLinksAddon();

      term.loadAddon(fitAddon);
      term.loadAddon(webLinksAddon);

      // Open terminal
      term.open(terminalRef.current);
      xtermRef.current = term;
      fitAddonRef.current = fitAddon;

      // Wait for terminal to be fully mounted before fitting
      setTimeout(() => {
        if (!mountedRef.current) return; // Don't fit if already unmounted
        if (fitAddon && terminalRef.current && xtermRef.current) {
          try {
            fitAddon.fit();
          } catch (err) {
            console.warn('Failed to fit terminal on mount:', err);
            // Retry once more after a longer delay
            setTimeout(() => {
              if (!mountedRef.current) return;
              try {
                fitAddon?.fit();
              } catch (retryErr) {
                console.error('Failed to fit terminal after retry:', retryErr);
              }
            }, 100);
          }
        }
      }, 50);
    }

    // Only create WebSocket if none exists or it's closed
    if (!ws || ws.readyState === WebSocket.CLOSED) {
      // Get auth token from localStorage (use correct key)
      const authToken = localStorage.getItem('ia_access_token') ||
        localStorage.getItem('ia_token') ||
        document.cookie.split('; ').find(row => row.startsWith('ia_auth='))?.split('=')[1];

      // Build WebSocket URL based on environment
      let wsUrl: string;
      if (import.meta.env.DEV) {
        // Development: Use localhost with port 4000
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${protocol}//${window.location.hostname}:4000/ws/terminal`;
      } else {
        // Production: Use API_BASE_URL to determine backend URL
        const backendUrl = API_BASE_URL || window.location.origin;
        const wsProtocol = backendUrl.startsWith('https://') ? 'wss:' : 'ws:';
        // Extract hostname from API_BASE_URL (remove http:// or https://)
        const hostname = backendUrl.replace(/^https?:\/\//, '').replace(/:\d+$/, '');
        wsUrl = `${wsProtocol}//${hostname}/ws/terminal`;
      }

      if (authToken) {
        wsUrl += `?token=${encodeURIComponent(authToken)}`;
      }

      console.log('[Terminal] Connecting to', wsUrl);

      term.writeln('\x1b[1;33m⟳ Connecting to terminal server...\x1b[0m');

      // Note: WebSocket constructor doesn't support credentials option like fetch
      // Cookies are automatically sent if same-origin or if server sends appropriate CORS headers
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        term.writeln('\x1b[1;32m✓ Connected to terminal server\x1b[0m');
        term.writeln('');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          switch (message.type) {
            case 'data':
              term.write(message.data);
              break;
            case 'exit':
              term.writeln(`\r\n\x1b[1;33mProcess exited with code ${message.code}\x1b[0m`);
              break;
            case 'pong':
              // Keepalive response
              break;
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        // Silently log the error - onclose will provide user feedback
        console.warn('WebSocket connection error (server may not be running):', error);
      };

      ws.onclose = (event) => {
        if (event.code === 4001) {
          term.writeln('\r\n\x1b[1;31m✗ Authentication required\x1b[0m');
          term.writeln('\x1b[1;33mRedirecting to login...\x1b[0m');
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
        } else if (event.code === 1000) {
          // Normal closure, don't show error
          term.writeln('\r\n\x1b[1;33mConnection closed\x1b[0m');
        } else {
          term.writeln('\r\n\x1b[1;31m✗ Disconnected from terminal server\x1b[0m');
        }
      };
    }

    // Always register terminal input handler (even on remount)
    const dataDisposable = term.onData((data) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'input',
          data: data,
        }));
      }
    });

    // Handle window resize
    const handleResize = () => {
      if (fitAddon && term) {
        try {
          fitAddon.fit();
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: 'resize',
              cols: term.cols,
              rows: term.rows,
            }));
          }
        } catch (err) {
          console.error('Failed to fit terminal:', err);
        }
      }
    };
    window.addEventListener('resize', handleResize);

    // Keepalive ping every 30 seconds
    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      mountedRef.current = false;
      dataDisposable.dispose();
      clearInterval(pingInterval);
      window.removeEventListener('resize', handleResize);

      // Only close WebSocket if it's actually OPEN (not CONNECTING)
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, 'Component unmounting');
      }

      // Don't dispose terminal - keep it for re-mount
    };
  }, []);

  // Final cleanup on unmount

  // Final cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up terminal and WebSocket on final unmount
      setTimeout(() => {
        if (!mountedRef.current) {
          // Only close if OPEN or CLOSING (not CONNECTING)
          if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CLOSING)) {
            wsRef.current.close();
            wsRef.current = null;
          }
          if (xtermRef.current) {
            try {
              xtermRef.current.dispose();
            } catch (e) {
              // Already disposed
            }
            xtermRef.current = null;
          }
        }
      }, 100);
    };
  }, []);

  return (
    <div className="terminal-container">
      <div className="terminal-header">
        <div className="terminal-title">
          <span className="terminal-icon">▶</span>
          intec.ai Terminal
        </div>
        <div className="terminal-controls">
          {onClose && (
            <button className="terminal-close" onClick={onClose} title="Close terminal">
              ✕
            </button>
          )}
        </div>
      </div>
      <div ref={terminalRef} className="terminal-content" />
    </div>
  );
};

export default Terminal;
